import { Fragment, useCallback, useEffect, useReducer, useRef, useState } from "react";
import Joyride, { type CallBackProps, type StoreHelpers } from "react-joyride";
import { connect } from "react-redux";

import { calcPlacement, type ITip } from "../../app-utils/help.js";
import { type User } from "../../graphql.js";
import { disableAllTips } from "../../redux/modules/auth.js";
import { type IAppState } from "../../redux/reducer.js";

interface ITipTourOwnProps {
  tips: ITip[];
  disable?: boolean; // when page not ready to show all feature e.g. global Genome Browser where user first needs to select a reference genome
}

interface ITipTourAppStateProps {
  session: User;
  showTips: boolean;
  firstLogin: boolean;
}

interface ITipTourDispatchProps {
  // methods used in mapDispatchToProps
  disableAllTips: () => void;
}

// combined props
export interface ITipTourProps extends ITipTourOwnProps, ITipTourAppStateProps, ITipTourDispatchProps {
  children: any;
}

function mapStateToProps(state: IAppState, ownProps: ITipTourOwnProps): ITipTourAppStateProps {
  return {
    session: state.auth.session,
    showTips:
      ownProps.disable !== true &&
      state.auth.session &&
      !state.auth.session.params.firstLogin &&
      state.auth.session.params.showTips,
    // autostart will be triggered only if this is first login
    firstLogin: state.auth.session && state.auth.firstLogin, // firstLogin is based on initial value of firstLogin session param, which is in local state and never modified so it guarantees that this is the first login
  };
}

function mapDispatchToProps(dispatch): ITipTourDispatchProps {
  return {
    disableAllTips: () => dispatch(disableAllTips()),
  };
}

interface TipTourBeaconProps {
  onClick: Function;
  tip: ITip;
}

function TipTourBeacon(props: TipTourBeaconProps) {
  const [position, setPosition] = useState(calcPlacement(props.tip));

  useEffect(() => {
    setPosition(calcPlacement(props.tip));
  }, [props]);

  return position ? (
    <button
      className="joyride-beacon show-all-beacons"
      style={{
        left: position.xPos,
        position: "fixed",
        top: position.yPos,
      }}
      onClick={() => props.onClick(props.tip)}
    >
      <span className={`joyride-beacon__inner`}>?</span>
      <span className={`joyride-beacon__outer`} />
    </button>
  ) : null;
}

/**
 * Wrapper component around a page that shows some tips with highlighted beacons.
 */
function TipTour(props: ITipTourProps) {
  const joyrideHelpers = useRef<StoreHelpers>();
  const intervalId = useRef<number>(-1);

  const [isShown, setShow] = useState(false);
  const [run, setRun] = useState(props.firstLogin);
  const [stepIndex, setStepIndex] = useState(0); // controlled tour with controlled step index
  //const [tips, setTips] = useState<ITip[]>(props.tips);

  // special effect to let some time for the beacons to be mounted until we show the tips
  useEffect(() => {
    if (props.session) {
      setTimeout(() => {
        // only switch on the tips with a delay, but do not accidentally switch them off again that way...
        if (props.showTips) {
          setShow(props.showTips);
        }
      }, 2000);
    }
  }, [props.showTips, props.session]);

  // Hack to forceUpdate
  const [, forceUpdate] = useReducer((x) => x + 1, 0);

  useEffect(() => {
    function cleanup() {
      clearInterval(intervalId.current);
      intervalId.current = -1;
    }

    if (props.showTips === true) {
      if (intervalId.current < 0) {
        intervalId.current = window.setInterval(() => forceUpdate(), 1000);
      }
    } else {
      cleanup();
    }

    setTimeout(() => setShow(props.showTips), 200);

    return cleanup;
  }, [props.showTips]);

  function handleBeaconClick(clickedTip: ITip) {
    if (joyrideHelpers.current) {
      const stepIndex: number = props.tips.findIndex((tip) => tip.target === clickedTip.target);
      setStepIndex(stepIndex);
      setRun(true); // make sure the tour runs, in case it was stopped already
    }
  }

  useEffect(() => {
    if (joyrideHelpers.current) {
      joyrideHelpers.current.go(stepIndex);
    }
  }, [stepIndex]);

  // in the following callback we do control the step index ourselves because the tour is controlled.
  // we also make use of "params.index === stepIndex" because the .go() method does trigger a "next"/"prev" action, exactly like when we click the "Next"/"Prev" buttons
  const tipCallback = useCallback(
    function tipCallback(params: CallBackProps) {
      if (
        params.action === "close" ||
        params.action === "esc" ||
        params.action === "stop" ||
        params.type === "finished" ||
        params.action === "reset"
      ) {
        setRun(false);
      } else if (params.action === "next" && params.type === "step:after" && params.index === stepIndex) {
        // next button
        if (stepIndex === params.size - 1) {
          // controlled tour, we are in the last tip of the list
          setRun(false);
        } else {
          setStepIndex(stepIndex + 1);
        }
      } else if (params.action === "prev" && params.type === "step:after" && params.index === stepIndex) {
        // back button
        setStepIndex(stepIndex - 1);
      } else if (params.action === "skip") {
        props.disableAllTips();
      }
    },
    [stepIndex],
  );

  // react-joyride library has a different concept, it shows only one single beacon at a time, so we take over
  // the display of all beacons and therefore we hide the library ones with "disableBeacon"
  const tips: ITip[] = props.tips.map((tip) => ({ ...tip, isFixed: true, disableBeacon: true })); // disableBeacon set to true so that the Joyride component does not show them

  return (
    <>
      {isShown === true && (
        <Fragment>
          {tips.map((tip) => (
            <TipTourBeacon tip={tip} onClick={handleBeaconClick} key={`tip-beacon-${tip.target}`} />
          ))}
          <Joyride
            steps={tips || []}
            run={run}
            disableOverlay={true}
            showSkipButton={true}
            continuous={true}
            locale={{ back: "Back", close: "Close", last: "Done", next: "Next Tip", skip: "Disable all tips" }}
            callback={tipCallback}
            getHelpers={(h) => (joyrideHelpers.current = h)}
            stepIndex={stepIndex}
            styles={joyRideStyles as any}
            floaterProps={joyRideFloaterStyles}
            disableScrollParentFix
            disableScrolling
          />
        </Fragment>
      )}

      {props.children}
    </>
  );
}

export const joyRideFloaterStyles = {
  disableAnimation: true,
  styles: {
    arrow: {
      length: 12,
      spread: 25,
    },
  },
};

export const joyRideStyles = {
  options: {
    zIndex: 600, // corresponds to $zIndex-popover
  },
  tooltip: {
    borderRadius: 4,
    boxSizing: "border-box",
    color: "#575757",
    padding: 20,
    position: "relative",
    width: 450,
    boxShadow: "0px 0px 10px 2px grey",
  },
  tooltipContainer: {
    textAlign: "left",
  },
  tooltipTitle: {
    fontSize: 18,
    margin: "0 0 10px 0",
    borderBottom: "1px solid #2988cc",
    color: "#242526",
    paddingBottom: 6,
  },
  tooltipContent: {
    fontSize: 14,
    padding: 0,
  },
  tooltipFooter: {
    alignItems: "center",
    display: "flex",
    justifyContent: "space-between",
    marginTop: 15,
  },
  buttonNext: {
    backgroundColor: "#3c97d8",
    color: "#fff",
    padding: "6px 12px",
    borderRadius: 4,
    fontSize: 14,
  },
  buttonBack: {
    color: "#2988cc",
    fontSize: 14,
  },
  buttonClose: {
    color: "#9da0a6",
    height: 10,
    padding: 0,
    position: "absolute",
    right: 20,
    top: 20,
    width: 10,
    fontSize: 25,
    fontWeight: "bold",
  },
  buttonSkip: {
    color: "#9da0a6",
    fontSize: 14,
  },
};
export default connect<ITipTourAppStateProps, ITipTourDispatchProps, ITipTourOwnProps>(
  mapStateToProps,
  mapDispatchToProps,
)(TipTour);
